import { Grid, MenuItem, Select, SelectChangeEvent, Table, TableBody, Typography } from "@mui/material";
import * as api from "api/Core";
import { DefaultListParameters } from "assets/appConfig";
import { AppMessageBox } from "components/AppMessageBox";
import IconButtonDownload from "components/Buttons/IconButtonDownload";
import { SpinnerIndicator } from "components/CallServer";
import { exportToXLSX } from "components/ExportToExcel";
import MuiMessageBox from "components/MuiExtras/MessageBox/MuiMessageBox";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiPagination from "components/MuiExtras/MuiPagination";
import MuiSelectFilter from "components/MuiExtras/MuiSelectFilter";
import MuiMuted from "components/MuiExtras/Typography/MuiMuted";
import HtmlUtils from "lib/HtmlUtils";
import LibUtils from "lib/LibUtils";
import { DashboardGraphByGroupModel, DashboardKnowledgeIndicatorsSummaryColumn, DashboardKnowledgeTestAnalysisDetailColumn, DashboardKnowledgeTestAnalysisFilter, DashboardKnowledgeTestAnalysisSummaryColumn, DashboardTopicModel, PagedResult } from "models/ApiModels";
import moment from "moment";
import * as React from "react";
import { WithTranslation } from "react-i18next";
import { RouteComponentProps, withRouter } from "react-router";
import { RoutePaths } from "routes/RouteConfig";
import { GraphLevels, KnowledgeTestGraph } from "views/Dashboards/components/KnowledgeCommons";
import { RenderTopicModelList, RenderTopicModelListEvents } from "views/Dashboards/components/RenderTopicModelList";
import { RenderUserModelListEvents } from "views/Dashboards/components/RenderUserModelList";
import { KnowledgeTestAdminListParameters } from "views/KnowledgeTest/KnowledgeTestAdminList";

/////////////////////////////////////////////////////////////////////////////////////

interface IGraphProps extends RenderUserModelListEvents, RenderTopicModelListEvents {
    filter: DashboardKnowledgeTestAnalysisFilter;
}

interface IGraphState {
    data: DashboardGraphByGroupModel;
    loading: boolean;
    lastRequestDate: Date;
}

type Props = IGraphProps & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////

export class KnowledgeTestAnalysisAnsweredGraph extends React.PureComponent<Props, IGraphState>{

    private selectedBarIndex: number = null;

    constructor(props: Props) {
        super(props);

        this.state = {
            data: null,
            loading: true,
            lastRequestDate: new Date()
        };
    }

    private loadData() {
        api.getDashboardKnowledgeTestAnalysis({
            columnToSummary: DashboardKnowledgeTestAnalysisSummaryColumn.Answered,
            detailColumn: DashboardKnowledgeTestAnalysisDetailColumn.Graph,
            ...this.props.filter,
            range: null,
        })
            .then((result) => {
                this.setState({ data: (result as DashboardGraphByGroupModel), loading: false });
            })
            .catch((error) => {
                this.setState({ data: null, loading: false });
            });
    }

    private handleBarSelected = (index: number) => {
        this.selectedBarIndex = index;
        this.setState({ lastRequestDate: new Date() });
    };

    public componentDidMount() {
        this.loadData();
    }

    public componentDidUpdate(prevProps: IGraphProps) {
        if (!LibUtils.isEqual(this.props.filter, prevProps.filter)) {
            this.loadData();
        }
    }

    ///
    public render() {
        const { data, loading } = this.state;

        if (loading)
            return <SpinnerIndicator />;

        if (data == null)
            return <AppMessageBox resource="LoadGraphDataFailed" />;

        return (
            <>
                <MuiContainer margin="dense" >
                    <Grid container justifyContent="space-between">
                        <Grid item>
                            <Typography component="p" variant="h6"><b>{this.props.t("Views.Dashboards.KnowledgeTestAnalysis.AnsweredGraph.HeaderTitle").toString().toLocaleUpperCase()}</b></Typography>
                            <MuiMuted>
                                <small>
                                    <span>{this.props.t("Views.Dashboards.KnowledgeTestAnalysis.AnsweredGraph.HeaderSubtitle")}</span>
                                </small>
                            </MuiMuted>
                        </Grid>
                    </Grid>
                </MuiContainer>

                <MuiContainer margin="dense" >
                    <KnowledgeTestGraph
                        tile={DashboardKnowledgeIndicatorsSummaryColumn.Result}
                        data={data}
                        onBarSelected={this.handleBarSelected}
                    />
                </MuiContainer>

                <KnowledgeTestAnalysisAnsweredList
                    {...this.props}
                    filter={this.props.filter}
                    selectedBar={this.selectedBarIndex}
                    lastRequestDate={this.state.lastRequestDate}
                />

            </>
        );
    }
}

/////////////////////////////////////////////////////////////////////////////////////////////

interface IListState {
    dialog: any;
    data: PagedResult<any>;
    offset: number;
    loading: boolean;
    primaryListOption: DashboardKnowledgeTestAnalysisDetailColumn;
    secondaryListOption: number;

}

interface IListProps extends RenderUserModelListEvents, RenderTopicModelListEvents {
    filter: DashboardKnowledgeTestAnalysisFilter;
    selectedBar: number;
    lastRequestDate: Date;
}

type ListAllProps = IListProps & RouteComponentProps & WithTranslation;

class KnowledgeTestAnalysisAnsweredListComponent extends React.PureComponent<ListAllProps, IListState> {

    constructor(props: ListAllProps) {
        super(props);

        this.state = {
            dialog: null,
            data: null,
            offset: null,
            loading: true,
            primaryListOption: DashboardKnowledgeTestAnalysisDetailColumn.TopicExausted,
            secondaryListOption: null
        };
    }

    ///
    private res(r: string) {
        return this.props.t("Views.Dashboards.KnowledgeTestAnalysis.AnsweredList." + r);
    }

    ///
    private loadData(offset: number, scrollToStartOfList: boolean = false) {

        this.setState({ loading: true });

        api.getDashboardKnowledgeTestAnalysis(
            {
                columnToSummary: DashboardKnowledgeTestAnalysisSummaryColumn.Answered,
                detailColumn: this.state.primaryListOption,
                range: { offset, take: DefaultListParameters.viewRecordsByPage },
                selectedValue: this.state.secondaryListOption,
                ...this.props.filter
            })
            .then((result) => {
                this.setState({ data: result, loading: false, offset: offset }, () => {
                    if (scrollToStartOfList) HtmlUtils.scrollToElement("divList");
                });

            })
            .catch((error) => {
                this.setState({ data: null, loading: false, offset: 0 });
            });
    }

    ///
    private handleExport = () => {
        api.getDashboardKnowledgeTestAnalysis(
            {
                columnToSummary: DashboardKnowledgeTestAnalysisSummaryColumn.Answered,
                detailColumn: this.state.primaryListOption,
                range: { offset: 0, take: 32767 },
                selectedValue: this.state.secondaryListOption,
                ...this.props.filter
            })
            .then((result) => {

                const rs = result.items as DashboardTopicModel[];
                const csv = rs.map((v) => { return { name: v.name, numberOfTests: v.detailCount, percent: v.percent }; });
                const fileName = 'KnwoledgeTestAnalysisAnswered-' + moment().toISOString();

                if (csv.length > 0) exportToXLSX(csv, fileName);
            });
    };

    ///
    private handleListOption = (e: SelectChangeEvent<DashboardKnowledgeTestAnalysisDetailColumn>) => {
        this.setState({
            primaryListOption: e.target.value as DashboardKnowledgeTestAnalysisDetailColumn,
            secondaryListOption:
                e.target.value == DashboardKnowledgeTestAnalysisDetailColumn.TopicExaustedLevel
                    ? GraphLevels(this.props.i18n).find(p => p.column == DashboardKnowledgeIndicatorsSummaryColumn.Result).options[0].key
                    : null
        }, () => this.loadData(0));
    };

    ///
    private handleSecondaryListOption = (e: SelectChangeEvent<number>) => {
        this.setState({ secondaryListOption: e.target.value as number }, () => this.loadData(0));
    };

    ///
    private getPrimaryListOptions() {

        var result: Array<{ key: DashboardKnowledgeTestAnalysisDetailColumn, text: string; }> = [];
        result.push({ key: DashboardKnowledgeTestAnalysisDetailColumn.TopicExausted, text: this.res("ListOp1") });
        result.push({ key: DashboardKnowledgeTestAnalysisDetailColumn.TopicExaustedLevel, text: this.res("ListOp2") });
        return result;
    }

    ///
    private getSecondaryListOptions() {
        var filter = GraphLevels(this.props.i18n).find(p => p.column == DashboardKnowledgeIndicatorsSummaryColumn.Result);
        return filter.options;
    }

    ///
    private computeListOptions() {
        const primaryListOption = (this.props.selectedBar == null)
            ? DashboardKnowledgeTestAnalysisDetailColumn.TopicExausted
            : DashboardKnowledgeTestAnalysisDetailColumn.TopicExaustedLevel;
        return { primaryListOption: primaryListOption, secondaryListOption: this.props.selectedBar };
    }

    ///
    private selectTopicHandler = (topicId: string, name: string, thumbnailUrl: string) => {
        this.props.history.push(RoutePaths.KnowledgeTestAdminList, {
            topic: {
                key: topicId,
                label: name,
                thumbnailUrl: thumbnailUrl,
                canDelete: true,
            },
        } as KnowledgeTestAdminListParameters);
    };

    ///
    private renderHeader() {

        return (
            <div id="divList" >
                <MuiContainer margin="dense" >
                    <Grid container justifyContent="space-between" alignItems="center">
                        <Grid item>

                            <Select
                                value={this.state.primaryListOption}
                                onChange={this.handleListOption}
                            >
                                {
                                    this.getPrimaryListOptions().map((v, index) => <MenuItem key={index} value={v.key}>{v.text}</MenuItem>)
                                }
                            </Select>

                        </Grid>

                        {
                            this.state.primaryListOption == DashboardKnowledgeTestAnalysisDetailColumn.TopicExaustedLevel
                                ? (
                                    <Grid item>
                                        <MuiSelectFilter
                                            value={this.state.secondaryListOption}
                                            onChange={this.handleSecondaryListOption}
                                        >
                                            {
                                                this.getSecondaryListOptions().map((v, index) => <MenuItem key={index} value={v.key}>{v.text}</MenuItem>)
                                            }
                                        </MuiSelectFilter>
                                    </Grid>
                                )
                                : null
                        }
                    </Grid>
                </MuiContainer>
            </div>
        );
    }

    ///
    private renderListBody() {
        const { data: { items }, offset } = this.state;
        return (
            <RenderTopicModelList
                indexOffset={offset}
                items={items}
                detailCountText={{ singular: this.props.t("Views.Dashboards.Common.DetailCountTest1"), plural: this.props.t("Views.Dashboards.Common.DetailCountTest2") }}
                onSelectTopic={this.selectTopicHandler}
            />
        );
    }

    ///
    private renderList() {
        const { data: { total }, dialog } = this.state;

        return (
            <MuiContainer margin="normal">
                <div>
                    {dialog}
                    <br />
                    {this.renderHeader()}

                    {
                        total == 0
                            ? <MuiMessageBox variant="warning" message={this.res("EmptyData")} />
                            : (
                                <>
                                    <MuiContainer>
                                        <Table size="small">
                                            <TableBody >
                                                {this.renderListBody()}
                                            </TableBody>
                                        </Table>
                                    </MuiContainer>
                                    {
                                        total > 0 &&

                                        <Grid container justifyContent="center" alignItems="center">
                                            <Grid item xs>
                                                <MuiPagination
                                                    limit={DefaultListParameters.viewRecordsByPage}
                                                    offset={this.state.offset}
                                                    total={total}
                                                    onClick={(e, offset) => this.loadData(offset, true)}
                                                />
                                            </Grid>
                                            <Grid item>
                                                <IconButtonDownload onClick={this.handleExport} />
                                            </Grid>
                                        </Grid>
                                    }
                                </>
                            )
                    }
                </div>
            </MuiContainer >
        );
    }

    ///
    private updateListOption() {
        this.setState({ ...this.computeListOptions() }, () => this.loadData(0));
    }

    ///
    private checkForUpdateState(prevProps: IListProps) {
        if (!LibUtils.isEqual(this.props, prevProps)) {
            this.updateListOption();
        }
    }

    ///
    public componentDidMount() {
        this.updateListOption();
    }

    ///
    public componentDidUpdate(prevProps: IListProps) {
        this.checkForUpdateState(prevProps);
    }

    ///
    public render() {

        return (
            <div>
                {
                    this.state.loading
                        ? <SpinnerIndicator />
                        : this.state.data == null
                            ? <AppMessageBox resource="LoadListDataFailed" />
                            : this.renderList()
                }
            </div>
        );
    }
}

const KnowledgeTestAnalysisAnsweredList = withRouter(KnowledgeTestAnalysisAnsweredListComponent);